import { format as dateFnsFormat, parseISO, addHours } from "date-fns";
import { tr } from "date-fns/locale";

/**
 * Gets the current device timezone offset in hours
 * @returns The timezone offset in hours (e.g., +3 for Turkey)
 */
export const getDeviceTimezoneOffset = (): number => {
  const offsetMinutes = new Date().getTimezoneOffset();
  // Convert to hours and invert (since getTimezoneOffset returns the opposite of what we need)
  return -(offsetMinutes / 60);
};

/**
 * Formats a date string or Date object to display in the device's local timezone
 * @param date Date string or Date object to format
 * @param formatString The date-fns format string
 * @param options Additional options
 * @returns Formatted date string in local timezone
 */
export const formatInDeviceTimezone = (
  date: string | Date,
  formatString: string = "dd.MM.yyyy HH:mm",
  options: { locale?: Locale } = { locale: tr }
): string => {
  // Convert to Date if string was provided
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  
  // Format date with the device's timezone
  return dateFnsFormat(dateObj, formatString, options);
};

/**
 * Converts a UTC date to the device's local timezone
 * @param utcDate Date in UTC
 * @returns Date adjusted to device timezone
 */
export const utcToDeviceTimezone = (utcDate: Date | string): Date => {
  const dateObj = typeof utcDate === "string" ? parseISO(utcDate) : utcDate;
  const timezoneOffset = getDeviceTimezoneOffset();
  
  // Apply the timezone offset to the UTC date
  return addHours(dateObj, timezoneOffset);
};

/**
 * Adjusts a date displayed in the UI to match the device's timezone
 * This is useful when reading dates from the database that are stored in UTC
 * @param dbDate Date from the database (presumed to be in UTC)
 * @returns Date adjusted to device timezone
 */
export const adjustDateFromDB = (dbDate: string | Date): Date => {
  return utcToDeviceTimezone(dbDate);
}; 